import {
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  HostBinding,
  ChangeDetectorRef,
  AfterViewInit,
  Directive,
} from "@angular/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import {
  ProfilePromiseActionService,
  BaseTableDefinitionFactory,
  CurrencyPairGroupLookupService,
  BfxBlotterAction,
} from "shared/cubiq-services";

import { BfxBaseBlotter } from "../bfx-base-blotter/bfx-base-blotter";
import { BfxBaseBlotterContextMenuService } from "../bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { BfxFindFilterView } from "./bfx-find-filter-view";
import { BfxFindService } from "./bfx-find.service";
import { GoldenLayoutComponent } from "../golden-layout/golden-layout-component";
import { GoldenLayoutPopout } from "../golden-layout/golden-layout-popout";
import { GoldenLayoutService } from "app/golden-layout/golden-layout-service";
import { LogoutNotificationService } from "../bfx-auth/logout-notification.service";
import {
  FilterData,
  ForwardFirstLabelValue,
  TimezoneCdLabelValue,
  TradeTypeLabelValue,
} from "./bfx-find.constants";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
import { BfxOrderEntryTimeControlModel } from "../bfx-interface-controls/models/bfx-interface-controls.models";
import { LoggerService } from "@bfx/logger";
import { Button } from "@bfx/primeng/button";
import { Calendar } from "@bfx/primeng/calendar";
import { DataTable } from "@bfx/primeng/datatable";
import { BfxClipboardService } from "shared/helper-services/bfx-clipboard.service";
import { BfxBlotterOrders } from "../bfx-blotter-orders/bfx-blotter-orders";
import { OrdersTableMode } from "../../shared/cubiq-services/model/OrdersTableMode";
const logger = LoggerService.for(__dirname);

@Directive()
export abstract class BfxFind<T extends CubiqTS.Encodable, F extends BaseTableDefinitionFactory>
  implements OnInit, AfterViewInit, OnDestroy, GoldenLayoutComponent
{
  public control: any = {
    mode: true,
    searchPaneVisible: false,
    embeddedBlotterLoaded: false,
    containerHeight: 324,
    scrollbarConfig: {
      suppressScrollX: false,
    },
    filterView: {
      scrollbarConfig: {
        suppressScrollX: true,
      },
    },
  };

  public currentLadderBlotter: any;

  public parentPopup: GoldenLayoutPopout;

  public selectedIds: any;

  @ViewChild("searchButton", { static: true })
  protected searchButton: Button;

  @ViewChild("filterViewPane", { static: true })
  protected filterViewPane: ElementRef;

  @ViewChild("favoriteDt", { static: true })
  protected favoriteDt: DataTable;

  @HostBinding("class.filter-view-shown")
  public filterViewShown: boolean = true;

  @HostBinding("class.filter-view-hidden")
  public filterViewHidden: boolean = false;

  public filterViewHasScroll: boolean = false;

  @HostBinding("class.find")
  public readonly _bemId: boolean = true;

  protected filterViewResponseObservableSub: any;

  private _captionLabel: string = "";

  protected favoriteConfig = {};
  public favoriteSelection: any = null;
  public favoriteData: any[];
  public favoriteEmptyMessage = `No saved favourites`;

  public resultCount: number = null;

  public abstract embeddedBlotter: BfxBaseBlotter;
  protected abstract filterView: BfxFindFilterView;
  protected abstract embeddedOrderColumnAllowListOverrideConfig;
  protected embeddedBlotterProfileConfig: any = null;

  protected abstract readonly profileKey: string;
  protected abstract readonly profileVersion: string;

  protected logoutKilling = false;
  public inProgress = false;
  public rowSelectionCount: number = 0;

  constructor(
    protected _clipboardService: BfxClipboardService,
    protected cd: ChangeDetectorRef,
    protected ngZone: NgZone,
    protected elementRef: ElementRef,
    protected goldenLayoutService: GoldenLayoutService,
    protected findService: BfxFindService,
    contextMenuService: BfxBaseBlotterContextMenuService<any>,
    protected profileActionService: ProfilePromiseActionService,
    protected logoutNotificationService: LogoutNotificationService,
    protected userProfileService: BfxUserProfileService,
    public tableDefinitionFactory: F,
    protected currencyPairGroupService: CurrencyPairGroupLookupService,
  ) {
    this.logoutNotificationService.getLogoutNotification().subscribe(
      (v) => {
        this.logoutKilling = true;

        // don't suppress logout!
        v.res(true);
      },
      () => {
        /* */
      },
      () => {
        /* */
      },
    );
  }

  ngOnInit(): void {
    this.embeddedBlotter.embeddedTableMode = true;
    this.embeddedBlotter.embeddedTableModeContextMenu = true;

    this.profileActionService.getProfileConfig(`${this.profileKey}`).then(
      (value: string) => {
        let config: any = null;

        if (value != "" && value != null) config = JSON.parse(value);

        if (config && config.version == this.profileVersion) {
          // perfer direct instance (saved on logout/profile save) to last-best global config
          if (this.embeddedBlotterProfileConfig == null)
            this.embeddedBlotterProfileConfig = config.blotterConfiguration;

          this.favoriteConfig = config.favoriteConfig ? config.favoriteConfig : {};
          this.restoreFavoriteConfig(this.favoriteConfig);
        }

        this.showResults([], this.embeddedBlotterProfileConfig, true);
      },
      () => {
        logger.out.warn(`${this.constructor.name}: failed to load profile config`);
        this.showResults([], null, true);
      },
    );
  }

  ngAfterViewInit() {
    this.embeddedBlotter.topMenuView.nativeElement.remove();
  }

  handleSearchClick() {
    if (this.inProgress) {
      return;
    }

    this.inProgress = true;
    this.clearResults();
    this.embeddedBlotter.setEmbeddedTableLoadPending();
    this.findService.requestFilterView();
  }

  // Golden layout popout component creation callback
  glInit(_data: any, _container: any, _state: any): void {
    /* */
  }

  private embeddedBlotterInit() {
    if (this.embeddedBlotter instanceof BfxBlotterOrders) {
      // FXBFXOM-7896 force find to dealt mode
      const ordersBlotter = this.embeddedBlotter as BfxBlotterOrders;
      ordersBlotter.mode = OrdersTableMode.DEALT;
    }
  }

  public get captionLabel(): string {
    return this._captionLabel;
  }

  public set captionLabel(captionLabel: string) {
    this._captionLabel = captionLabel;
    this.cd.markForCheck();
  }

  setParentPopup(parentPopout: GoldenLayoutPopout) {
    parentPopout.setShowDock(true);
  }

  protected clearResults() {
    this.captionLabel = "";
    this.showResults([], null, true);
  }

  showResults(results: Array<any>, config: any = null, isClear: boolean = false): void {
    results = this.decorateWithPrimaryKey(results);

    this.embeddedBlotter.setEmbeddedTable(
      results,
      config ? config : this.embeddedBlotter.getComponentConfiguration(),
      this.embeddedOrderColumnAllowListOverrideConfig,
    );
    this.control.embeddedBlotterLoaded = true;

    this.embeddedBlotterInit();

    if (isClear) {
      this.resultCount = null;
    } else {
      this.resultCount = results.length;
    }

    this.cd.detectChanges();
    setTimeout(this.embeddedBlotter.restoreSortFromConfig.bind(this.embeddedBlotter), 500);
  }

  decorateWithPrimaryKey(results: any[]): any[] {
    for (let index = 0; index < results.length; index++) {
      let result = results[index];
      let primaryKey = result["primaryKey"];
      if (null == primaryKey) {
        result["primaryKey"] = result.orderId;
      }
    }
    return results;
  }

  close(_$event: MouseEvent) {
    this.parentPopup.close();
  }

  onClose(): boolean {
    if (this.inProgress && !this.logoutKilling) {
      return false;
    }

    this.filterViewResponseObservableSub.unsubscribe();
    this.updateConfiguration();
  }

  ngOnDestroy() {
    this.filterViewResponseObservableSub.unsubscribe();
  }

  public getComponentConfiguration(): any {
    return this.embeddedBlotter.getComponentConfiguration();
  }

  public restoreFromConfiguration(config: any): void {
    if (config != null) this.embeddedBlotterProfileConfig = config;
  }

  updateConfiguration(): void {
    // save config
    this.embeddedBlotterProfileConfig = this.embeddedBlotter.getComponentConfiguration();
    let profileValue = JSON.stringify({
      version: this.profileVersion,
      blotterConfiguration: this.embeddedBlotterProfileConfig,
      favoriteConfig: this.getFavoriteConfiguration(),
    });
    let profileSettings: Array<CubiqTS.ProfileSettingImpl> = [];
    profileSettings.push(
      this.userProfileService.createProfileSettingObject(this.profileKey, profileValue),
    );

    this.profileActionService.updateProfile(profileSettings, false).then(
      () => {
        logger.out.info(
          `${this.constructor.name}.updateConfiguration(): ${this.profileKey} saved.`,
        );
      },
      (reason) => {
        logger.out.warn(`${this.constructor.name}.updateConfiguration() failed ${reason}`);
      },
    );
  }

  getFavoriteConfiguration(): any {
    if (this.favoriteConfig != null) {
      return this.favoriteConfig;
    }

    return {};
  }

  handleSelectSearch(): void {
    if (this.searchButton) {
      this.searchButton["el"].nativeElement.focus();
    }
  }

  altEnterSearch(event): void {
    if (event.defaultPrevented) {
      return;
    }
    let handled = false;
    if (event.key !== undefined) {
      if (event.key === "Enter" && event.altKey) {
        this.handleSearchClick();
      }
    } else if (event.keyIdentifier !== undefined) {
      if (event.keyIdentifier === "Enter" && event.altKey) {
        this.handleSearchClick();
      }
    } else if (event.keyCode !== undefined) {
      if (event.keyCode === 13 && event.altKey) {
        this.handleSearchClick();
      }
    }
    if (handled) {
      event.preventDefault();
    }
  }

  handleClearClick(): void {
    this.findService.requestClear();

    this.clearResults();
  }

  handleExport(): void {
    this.findService.requestExport(this.embeddedBlotter);
  }

  handleSave(): void {
    let existingFavorites = this.getFavoriteConfiguration();

    this.findService.createNewFavorite(
      this.createNewFavorite.bind(this),
      new Set(
        Object.keys(existingFavorites).map((existingFavorite) => {
          return existingFavorite.toLowerCase();
        }),
      ),
    );
  }

  /**
   * Callback to create a new favorite with a given name
   * @param name the name of the new favorite to be added
   */
  protected createNewFavorite(name: string): void {
    this.favoriteConfig[name] = this.filterView.saveData();

    this.updateConfiguration();

    this.restoreFavoriteConfig(this.favoriteConfig);
  }

  restoreFavoriteConfig(config: any): void {
    this.favoriteData = Object.keys(this.favoriteConfig)
      .sort((a, b) => {
        return a.toLowerCase() < b.toLowerCase() ? -1 : a.toLowerCase() > b.toLowerCase() ? 1 : 0;
      })
      .map((v) => {
        return { filterName: v };
      });

    Object.keys(this.favoriteConfig).forEach((k) => {
      this.favoriteConfig[k] = config[k];
    });
  }

  toggleViewPane(): void {
    this.filterViewShown = !this.filterViewShown;
    this.filterViewHidden = !this.filterViewHidden;
  }

  selectFavorite(data): void {
    let favoriteName = data.filterName;

    this.filterView.restoreData(this.favoriteConfig[favoriteName]);

    this.handleSearchClick();
  }

  removeFavorite(data): void {
    let favoriteName = data.filterName;

    delete this.favoriteConfig[favoriteName];

    this.updateConfiguration();

    this.restoreFavoriteConfig(this.favoriteConfig);
  }

  // strip data to a remotable form
  transformData(data: FilterData[]): any {
    return data.map((entry) => {
      return {
        label: entry.label,
        filterConditions: entry.filterConditions.map((filterCondition) => {
          return {
            to: (function () {
              if (filterCondition.hasValue == false || filterCondition.to == null) {
                return null;
              } else if (filterCondition.to instanceof Date) {
                return filterCondition.to;
              } else if (filterCondition.to instanceof BfxOrderEntryTimeControlModel) {
                return (
                  filterCondition.to.timeValue +
                  "-" +
                  (filterCondition.to.timezoneValue ? filterCondition.to.timezoneValue.value : "")
                );
              } else {
                return filterCondition.to.toString();
              }
            })(),
            from: (function () {
              if (filterCondition.hasValue == false || filterCondition.from == null) {
                return null;
              } else if (filterCondition.from instanceof Date) {
                return filterCondition.from;
              } else if (filterCondition.from instanceof BfxOrderEntryTimeControlModel) {
                return (
                  filterCondition.from.timeValue +
                  "-" +
                  (filterCondition.from.timezoneValue
                    ? filterCondition.from.timezoneValue.value
                    : "")
                );
              } else {
                return filterCondition.from.toString();
              }
            })(),
            value: function () {
              if (filterCondition.hasValue == false || filterCondition.value == null) {
                return null;
              } else if (Array.isArray(filterCondition.value)) {
                try {
                  if (filterCondition.fieldName === "ccyPairGroup") {
                    let pairs = new Set<string>();

                    filterCondition.value.forEach((filterValue) => {
                      // filter value { label: groupName, value: pairs[] }
                      let ccyGroup: CubiqTS.CurrencyPairGroupImpl;
                      ccyGroup = this.currencyPairGroupService.findByName(filterValue.label);
                      if (ccyGroup != null) {
                        ccyGroup.groupMembership.forEach((m) => {
                          pairs.add(m.baseCurrencyCd + m.nonBaseCurrencyCd);
                        });
                      } else if (Array.isArray(filterValue.value)) {
                        filterValue.value
                          .filter((v) => v?.value != null)
                          .forEach((v) => pairs.add(v.value));
                      }
                    });

                    let ccyPairService = this.currencyPairGroupService.ccyPairService;
                    let expanded = ccyPairService.expandWildcardPairs([...pairs]).map((c) => {
                      return { value: c };
                    });

                    // Result is in format { value: Array<{ value: string }> }
                    return [{ value: expanded }];
                  }
                } catch (e) {
                  logger.out.error(
                    `${this.constructor.name}: Failed to expand ccy pair group` + ` values`,
                    e,
                  );
                }

                return filterCondition.value.map((filterValue) => {
                  let value;

                  if (filterValue.value == null) {
                    value = null;
                  } else if (Array.isArray(filterValue.value)) {
                    value = filterValue.value.map((nestedValue) => {
                      return {
                        value: nestedValue.value ? nestedValue.value.toString() : null,
                      };
                    });
                  } else if (filterValue.value instanceof Date) {
                    value = filterValue.value;
                  } else if (
                    filterCondition.fieldName === "requestedOrderType" ||
                    filterCondition.fieldName === "requestOrderType"
                  ) {
                    return {
                      type: filterValue.type,
                      value: filterValue.findValue ?? filterValue.value,
                    };
                  } else {
                    value = filterValue.value.toString();
                  }

                  return {
                    value: value,
                  };
                });
              } else if (filterCondition.value instanceof Date) {
                return filterCondition.value;
              } else if (filterCondition.value instanceof TimezoneCdLabelValue) {
                let filterValue = filterCondition.value;
                return filterValue.value.toString();
              } else if (filterCondition.value instanceof ForwardFirstLabelValue) {
                let filterValue = filterCondition.value;
                return filterValue.value.toString();
              } else if (filterCondition.value instanceof TradeTypeLabelValue) {
                let filterValue = filterCondition.value;
                return filterValue.value;
              } else if (filterCondition.value instanceof BfxOrderEntryTimeControlModel) {
                return (
                  filterCondition.value.timeValue +
                  "-" +
                  (filterCondition.value.timezoneValue
                    ? filterCondition.value.timezoneValue.value
                    : "")
                );
              } else {
                return filterCondition.value.toString();
              }
            }.bind(this)(),
            parseType: filterCondition.parseType,
            fieldName: filterCondition.fieldName,
            operator: filterCondition.operator,
          };
        }),
      };
    });
  }

  public undocked(): void {
    this.elementRef.nativeElement.ownerDocument.body.append(this.filterView.calendarPanelHelper);

    if (window != null) {
      this.filterView.calendarComponents.forEach((_calendar: Calendar) => {
        // FIXME: NG8
        // let overlay: Element = calendar.overlayViewChild.nativeElement;
        // overlay['style'].opacity = '1';
        // calendar.unbindDocumentClickListener();
        // calendar.documentClickListener = calendar.renderer.listen(window.getNativeWindow().document, 'click', (event) => {
        //     if (!calendar.datepickerClick && calendar.overlayVisible) {
        //         calendar.overlayVisible = false;
        //     }
        //     calendar.datepickerClick = false;
        //     calendar.cd.detectChanges();
        // });
      });
    }
  }

  public docked(): void {
    document.body.appendChild(this.filterView.calendarPanelHelper);
    this.filterView.calendarComponents.forEach((calendar: Calendar) => {
      calendar.unbindDocumentClickListener();
      calendar.bindDocumentClickListener();
    });
  }
}
